import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { tours } from "@/data/tours";
import { SeoHead } from "@/components/common/SeoHead";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { AnimatedSection } from "@/components/common/AnimatedSection";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Star, Clock, MapPin, ArrowRight, Search } from "lucide-react";

export default function ToursPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("All");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const regions = ["All", ...new Set(tours.map((t) => t.region))];

  const filteredTours = tours.filter((tour) => {
    const matchesSearch =
      tour.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tour.destination.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRegion = selectedRegion === "All" || tour.region === selectedRegion;
    return matchesSearch && matchesRegion;
  });

  return (
    <div id="main-content" className="min-h-screen bg-white">
      <SeoHead
        title="Tour Packages"
        description="Explore handpicked guided tours across Asia, Europe, Africa, and the Americas. Request a personalized quote from Tripsyfy's travel experts."
        canonical="https://tripsyfy.com/tours"
      />
      <Navbar />

      {/* Page header */}
      <section className="relative pt-28 pb-14 bg-primary" data-testid="tours-header">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <p className="text-sky-300 text-xs font-semibold tracking-widest uppercase mb-3">Explore the World</p>
          <h1 className="heading-xl text-white mb-3">Tour Packages</h1>
          <p className="text-base text-white/70 max-w-lg mb-8 leading-relaxed">
            Handpicked guided tours across the world's most beautiful destinations — tailored to your adventure level.
          </p>

          {/* Search */}
          <div className="max-w-md">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
              <Input
                placeholder="Search tours or destinations..."
                aria-label="Search tours or destinations"
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40 h-11 focus-visible:ring-white/30 focus-visible:border-white/40"
              />
            </div>
          </div>

          {/* Region filter chips */}
          <div className="flex flex-wrap gap-2 mt-5">
            {regions.map((region) => (
              <button
                key={region}
                onClick={() => setSelectedRegion(region)}
                className={`inline-flex items-center px-4 py-2 rounded-pill text-xs font-semibold transition-colors duration-[120ms] ${
                  selectedRegion === region
                    ? "bg-accent text-white shadow-sm"
                    : "bg-white/10 text-white/80 border border-white/20 hover:bg-white/20 hover:text-white"
                }`}
              >
                {region}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tours grid */}
      <section className="section-padding bg-surface-subtle" data-testid="tours-grid">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          {filteredTours.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-muted-foreground text-lg mb-4">No tours found matching your criteria.</p>
              <Button
                variant="outline"
                onClick={() => { setSearchTerm(""); setSelectedRegion("All"); }}
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
              {filteredTours.map((tour, i) => (
                <AnimatedSection key={tour.id} delay={Math.min(i * 0.06, 0.3)}>
                  <div className="group bg-white rounded-xl shadow-card hover:shadow-card-hover transition-shadow duration-200 overflow-hidden flex flex-col h-full">
                    <div className="relative overflow-hidden aspect-[4/3]">
                      <img
                        src={tour.image}
                        alt={tour.title}
                        className="w-full h-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.2,0,0,1)] group-hover:scale-105"
                        loading="lazy"
                      />
                      <Badge variant="accent" className="absolute top-3 left-3">
                        {tour.region}
                      </Badge>
                      <div className="absolute top-3 right-3 bg-white/95 rounded-lg px-2 py-1 flex items-center gap-1">
                        <Star className="h-3 w-3 fill-accent text-accent" />
                        <span className="text-xs font-bold text-primary">{tour.rating}</span>
                      </div>
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <div className="flex items-center gap-4 text-xs text-muted-foreground mb-2.5">
                        <span className="flex items-center gap-1.5">
                          <MapPin className="h-3.5 w-3.5 shrink-0" /> {tour.destination}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5 shrink-0" /> {tour.duration}
                        </span>
                      </div>
                      <h3 className="text-base font-bold text-primary mb-2 group-hover:text-accent transition-colors duration-[120ms] line-clamp-2">
                        {tour.title}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-4 flex-1 leading-relaxed">
                        {tour.description}
                      </p>
                      <div className="flex items-center justify-between pt-4 border-t border-border/60">
                        <div>
                          <p className="text-xs text-muted-foreground">From</p>
                          <p className="text-xl font-bold text-primary">${tour.price.toLocaleString()}</p>
                        </div>
                        <Link to={`/tours/${tour.id}`}>
                          <Button size="sm" variant="outline">
                            View Details <ArrowRight className="h-3.5 w-3.5" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
